// Create-time templates (spec §8, Decision 9): what a new survey's step list
// starts as. Templates are UX, not storage — the server only ever sees the
// resulting PUT /steps body, and the editor edits the rows like any other.

import type { PutStep } from "@/api/surveys";
import { presetByKey } from "@/features/presets/scale-presets";

export type SurveyTemplateKey = "feedback" | "poll" | "blank";

export interface SurveyTemplate {
    key: SurveyTemplateKey;
    label: string;
    description: string;
}

export const SURVEY_TEMPLATES: SurveyTemplate[] = [
    {
        key: "feedback",
        label: "Feedback",
        description: "A scored smiley rating, follow-up reasons split by score, and a free comment.",
    },
    { key: "poll", label: "Poll", description: "One question with a few answers to pick from. No score." },
    { key: "blank", label: "Blank", description: "Start from nothing and add steps yourself." },
];

export function templateSteps(key: SurveyTemplateKey): PutStep[] {
    switch (key) {
        case "feedback": {
            const preset = presetByKey("smiley-5");
            return [
                {
                    kind: "choice",
                    isMetric: true,
                    config: {
                        prompt: "How was your visit?",
                        preset: preset?.key ?? null,
                        options: preset?.options ?? [],
                    },
                },
                {
                    kind: "multi",
                    isMetric: false,
                    config: {
                        mode: "split",
                        promptNegative: "What went wrong?",
                        promptPositive: "What did you like?",
                        negative: [
                            { key: "wait", label: "Waiting time" },
                            { key: "food", label: "Food" },
                            { key: "staff", label: "Staff" },
                        ],
                        positive: [
                            { key: "food", label: "Food" },
                            { key: "staff", label: "Staff" },
                            { key: "speed", label: "Speed" },
                        ],
                        threshold: 2,
                    },
                },
                { kind: "text", isMetric: false, config: { prompt: "Anything else?" } },
            ];
        }
        case "poll":
            return [
                {
                    kind: "choice",
                    isMetric: false,
                    config: {
                        prompt: "Which would you pick?",
                        preset: null,
                        options: [
                            { key: "a", label: "Option A" },
                            { key: "b", label: "Option B" },
                            { key: "c", label: "Option C" },
                        ],
                    },
                },
            ];
        case "blank":
            return [];
    }
}
